import wayIsOpenTicket from './wayIsOpenTicket';
import { getInsuranceEligibilityFromForms } from './insuranceEligibility';

/**
 * Indicates whether a price-like value is greater than zero.
 * @param {number|string} amount - Amount to check.
 * @returns {boolean} Whether the amount is positive.
 */
const hasPositiveAmount = (amount) => Number(amount) > 0;

/**
 * Returns passenger-level insurance availability for the current purchase.
 * Quick purchase skips the passengers step, so it needs the same way-level
 * decision that the passenger form normally makes before saving passengers.
 * @param {object} params - features and purchase state.
 * @param {object} params.features - Tenant feature flags.
 * @param {object} params.purchase - Current purchase state.
 * @returns {object} Availability by way and aggregate enabled state.
 */
export function getQuickPurchaseInsuranceAvailability({ features = {}, purchase = {} }) {
  const passengerInsuranceEnabled = Boolean(features.PASSENGERS_INSURANCE_ENABLED);
  const qualifiesForInsurance = Boolean(purchase.qualifiesForInsurance);

  const canSelectOutgoingInsurance =
    passengerInsuranceEnabled &&
    qualifiesForInsurance &&
    hasPositiveAmount(purchase.insuranceOutgoingUnitAmount) &&
    !wayIsOpenTicket(purchase.departs);

  const canSelectIncomingInsurance =
    passengerInsuranceEnabled &&
    qualifiesForInsurance &&
    Boolean(purchase.roundTrip) &&
    hasPositiveAmount(purchase.insuranceIncomingUnitAmount) &&
    !wayIsOpenTicket(purchase.returns);

  return {
    enabled: canSelectOutgoingInsurance || canSelectIncomingInsurance,
    canSelectOutgoingInsurance,
    canSelectIncomingInsurance,
  };
}

/**
 * Builds the passenger insurance flags for quick purchase.
 * @param {object} params - features, purchase state and selected switch state.
 * @param {object} params.features - Tenant feature flags.
 * @param {object} params.purchase - Current purchase state.
 * @param {boolean} params.selected - Whether insurance should be selected.
 * @param {Array<object>} params.passengers - Passengers to validate for insurance eligibility.
 * @returns {{wantsOutgoingInsurance: boolean, wantsIncomingInsurance: boolean}}
 */
export function getQuickPurchaseInsuranceSelection({
  features = {},
  purchase = {},
  selected = false,
  passengers = [],
}) {
  const availability = getQuickPurchaseInsuranceAvailability({ features, purchase });
  const { hasIneligibleDoc } =
    selected && passengers.length
      ? getInsuranceEligibilityFromForms({ purchase: { passengers } })
      : { hasIneligibleDoc: false };
  const canSelectInsurance = selected && !hasIneligibleDoc;

  return {
    wantsOutgoingInsurance: Boolean(canSelectInsurance && availability.canSelectOutgoingInsurance),
    wantsIncomingInsurance: Boolean(canSelectInsurance && availability.canSelectIncomingInsurance),
  };
}

/**
 * Returns a stable key for an automatic quick-checkout insurance disable attempt.
 * @param {object} params - Attempt key params.
 * @param {string} params.token - Purchase token.
 * @param {boolean} params.usesPassengerInsurance - Whether passenger-level insurance is active.
 * @param {Array<string>} params.passengerDocs - Document values used for eligibility.
 * @returns {string} Attempt key.
 */
export function getQuickPurchaseInsuranceAutoDisableKey({
  token,
  usesPassengerInsurance,
  passengerDocs = [],
}) {
  return [token || '', usesPassengerInsurance ? 'passengers' : 'purchase', ...passengerDocs].join(
    '|',
  );
}

/**
 * Indicates whether quick checkout should attempt to auto-disable insurance.
 * @param {object} params - Auto-disable state.
 * @param {boolean} params.isOrchestrating - Whether quick checkout is still setting up.
 * @param {boolean} params.enablesInsurance - Whether insurance is available.
 * @param {boolean} params.selectedInsurance - Whether insurance is currently selected.
 * @param {boolean} params.insuranceDisabled - Whether documents make insurance unavailable.
 * @param {boolean} params.updatingInsurance - Whether an insurance update is in flight.
 * @param {string|null} params.attemptedKey - Last automatic disable attempt key.
 * @param {string} params.attemptKey - Current automatic disable attempt key.
 * @returns {boolean} Whether the effect should dispatch a disable request.
 */
export function shouldAutoDisableQuickPurchaseInsurance({
  isOrchestrating,
  enablesInsurance,
  selectedInsurance,
  insuranceDisabled,
  updatingInsurance,
  attemptedKey,
  attemptKey,
}) {
  return Boolean(
    !isOrchestrating &&
      enablesInsurance &&
      selectedInsurance &&
      insuranceDisabled &&
      !updatingInsurance &&
      attemptedKey !== attemptKey,
  );
}

/**
 * Returns true when any saved quick-checkout passenger currently has insurance selected.
 * @param {object} purchase - Current purchase state.
 * @returns {boolean} Whether passenger-level insurance is selected.
 */
export function hasSelectedPassengerInsurance(purchase = {}) {
  return Boolean(
    purchase.passengers?.some(
      (passenger) => passenger.wantsOutgoingInsurance || passenger.wantsIncomingInsurance,
    ),
  );
}
